/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect, useState } from 'react';
import { BehaviorSubject } from 'rxjs';

import {
  signIn as awsSignIn,
  signOut as awsSignOut,
  getCurrentUser,
  fetchAuthSession,
  confirmSignIn,
  confirmUserAttribute,
  sendUserAttributeVerificationCode,
  resetPassword,
  confirmResetPassword,
  AuthUser,
} from 'aws-amplify/auth';

import { IUserCredentials } from '../interfaces/auth.interface';

const cognitoUser$ = new BehaviorSubject<AuthUser | null>(null);


const clearLocalAuthState = () => {
  cognitoUser$.next(null);
  localStorage.removeItem('user');
};


const setCognitoUser = (state: AuthUser | null) => {
  cognitoUser$.next(state);
};


const currentAuthenticatedUser = async (): Promise<AuthUser> => {
  try {
    const session = await fetchAuthSession();

    if (!session.tokens?.accessToken) {
      throw new Error('No valid authentication session found');
    }

    const user = await getCurrentUser();

    cognitoUser$.next(user);

    return user;
  } catch (error) {
    cognitoUser$.next(null);

    throw error;
  }
};


const getCognitoUser = async (): Promise<AuthUser | null> => {
  try {
    return await currentAuthenticatedUser();
  } catch (error) {
    console.warn('No authenticated user found:', error);

    clearLocalAuthState();

    return null;
  }
};


const useCognitoUserState = (): [
  AuthUser | null,
  (user: AuthUser | null) => void
] => {
  const [value, setState] = useState<AuthUser | null>(
    cognitoUser$.getValue()
  );

  useEffect(() => {
    const subscription = cognitoUser$.subscribe((user) => {
      setState(user);
    });

    return () => subscription.unsubscribe();
  }, []);

  return [value, setCognitoUser];
};

const signIn = ({ username, password }: IUserCredentials) =>
  awsSignIn({
    username,
    password,
  });


const signOut = async (): Promise<void> => {
  try {
    await awsSignOut();
  } finally {
    clearLocalAuthState();
  }
};


const forgotPassword = (username: string) =>
  resetPassword({
    username: username.trim().toLowerCase(),
  });

const forgotPasswordSubmit = (
  username: string,
  code: string,
  password: string
) =>
  confirmResetPassword({
    username: username.trim().toLowerCase(),
    confirmationCode: code,
    newPassword: password,
  });

const verifyEmail = (code: string) =>
  confirmUserAttribute({
    confirmationCode: code,
    userAttributeKey: 'email',
  });

const resendCode = () =>
  sendUserAttributeVerificationCode({
    userAttributeKey: 'email',
  });

export async function completeNewPassword({
  newPassword,
}: {
  username: string;
  newPassword: string;
}) {
  return confirmSignIn({
    challengeResponse: newPassword,
  });
}

export default {
  signIn,
  signOut,
  currentAuthenticatedUser,
  completeNewPassword,
  setCognitoUser,
  getCognitoUser,
  useCognitoUserState,
  forgotPassword,
  forgotPasswordSubmit,
  verifyEmail,
  resendCode,
  clearLocalAuthState,
};